import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Mutex } from 'async-mutex';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { InspectModeRepository } from '../state/inspect-mode-repository.service';
import { ButtonToasterElement } from '../ui/notification-toaster/button-toaster-element/button-toaster-element.component';
import {
  CustomNotificationToastrComponent,
  ToasterPopupStyle,
} from '../ui/notification-toaster/custom-notification-toastr/custom-notification-toastr.component';
import {
  IconBackground,
  IconMessageToasterElement,
} from '../ui/notification-toaster/icon-message-toaster-element/icon-message-toaster-element.component';
import { CustomNotificationToasterV2Component } from '../ui/notification-toaster/custom-notification-toaster-v2/custom-notification-toaster-v2.component';
import { ButtonIconConfig } from '../standalones/components/pencil-button/pencil-button.component';
import { IconTypes } from '../standalones/components/pencil-icon/pencil-icon.component';
import { User } from '../models/user';
import { isObserverTabId } from './observer-mode.utils';
import { UserService } from './user.service';

const TOAST_BASE_CLASS = 'notification-popup';

interface OptionsButtonState {
  items: {
    text: string;
    onClick: () => void;
  }[];
}

export interface TopRightOptions extends OptionsButtonState {
  icon: string;
}

export interface BottomElementOptions extends OptionsButtonState {
  buttonText: string;
  menuTitle: string;
  onPrimaryButtonClick: () => void;
}

export const DEFAULT_NOTIFICAION_TIMEOUT = 7.5;

export enum NotificationIconTypes {
  SVG_ICON = 'svg-icon',
  LUCIDE_ICON = 'lucide-icon',
  MAT_ICON = 'mat-icon',
  SPINNER = 'spinner',
  USER = 'user',
}

export enum NotificationVersion {
  V1 = 'v1',
  V2 = 'v2',
}

export interface NotificationIconConfig {
  assetName?: string;
  type?: NotificationIconTypes;
  background?: IconBackground;
  outlined?: boolean;
  width?: number;
  height?: number;
  size?: number;
  user?: User;
  spinnerStyle?: Record<string, string>;
}

export interface NotificationActionButton {
  text: string;
  icon?: string;
  iconConfig?: ButtonIconConfig;
  onClick?: () => void;
  options?: OptionsButtonState;
  close?: boolean;
  /**
   * When set, the V2 builder does not apply the default `keyboard_arrow_right` icon
   */
  suppressDefaultActionIcon?: boolean;
}

export interface NotificationActionButtonsConfig {
  /**
   * primary action button on the right that has owr primary backbround
   */
  primary?: NotificationActionButton;
  /**
   * the secondary button with the light background
   */
  secondary?: NotificationActionButton;
  /**
   * if the button is link button
   */
  link?: NotificationActionButton;
}

export interface BaseNotificationData {
  style?: ToasterPopupStyle;
  timeout?: number;
  toastClass?: string;
  dismissible?: boolean;
  priority?: number;
  type?: NotificationType;
  unique?: boolean;
  notificationCode: string;
  showProgressBar: boolean;
  progressBarColor?: string;
  extendedTimeOut?: number;
  onActivateTick?: boolean;
  version?: NotificationVersion;
  allowActionsForObservers?: boolean;
}

export interface NotificationData extends BaseNotificationData {
  topElements: [IconMessageToasterElement];
  topRightOptions?: TopRightOptions;
  middleElements: IconMessageToasterElement[];
  bottomElements: ButtonToasterElement[];
  bottomElementsStacked?: boolean;
  bottomElementButtonOptions?: BottomElementOptions;
  showOnMobile?: boolean;
  width?: number;
  height?: string;
}

export interface V2NotificationData extends BaseNotificationData {
  title?: string;
  icon?: NotificationIconConfig;
  description?: string;
  actionButtonsConfig?: NotificationActionButtonsConfig;
  actionProgressBarObservable?: Observable<number>;
}

export abstract class BaseNotificationDataBuilder<T extends BaseNotificationData> {
  protected readonly _notificationData: T;

  constructor(notificationCode: string) {
    this._notificationData = {
      notificationCode,
      style: ToasterPopupStyle.INFO,
      timeout: undefined,
      dismissible: true,
      priority: 0,
      type: NotificationType.INFO,
      unique: false,
      showProgressBar: true,
      extendedTimeOut: 1000,
      onActivateTick: false,
      toastClass: '',
      progressBarColor: undefined,
    } as T;
  }

  abstract build(): T;

  /**
   * return the version of the notification v1 or v2
   */
  protected setVersion(version: NotificationVersion) {
    this._notificationData.version = version;
  }

  /**
   * Sets notification style
   * @param style ToasterPopupStyle
   * @returns this
   */
  style(style: ToasterPopupStyle): this {
    this._notificationData.style = style;
    return this;
  }

  /**
   * Sets the timeout for notification to live in seconds
   * @param number timeOut number of seconds
   * @returns this
   */
  timeOut(timeOut: number): this {
    this._notificationData.timeout = timeOut * 1000;
    return this;
  }

  /**
   * Sets additional style classes for the toastr (base class is added automatically).
   * @param toastClass string. Additional classes to add. Default is ""
   * @returns this
   */
  toastClass(toastClass: string): this {
    this._notificationData.toastClass = toastClass;
    return this;
  }

  /**
   * Whether the notification is dismissable or not
   * @param dismissable boolean. Default is true
   * @returns this
   */
  dismissable(dismissable: boolean): this {
    this._notificationData.dismissible = dismissable;
    return this;
  }

  /**
   * Sets the priority of the notification. Higher priority is shown on top of the stack
   * @param priority number. Default is 0
   * @returns this
   */
  priority(priority: number): this {
    this._notificationData.priority = priority;
    return this;
  }

  /**
   * Sets the type of notification (ERROR, REQUEST, WARNING, SUCCESS or INFO)
   * @param type NotificationType
   * @returns this
   */
  type(type: NotificationType): this {
    this._notificationData.type = type;
    return this;
  }

  /**
   * Whether to show more notification of the same type or not (globally set for now)
   * @param unique boolean.
   * @returns this
   */
  unique(unique: boolean): this {
    this._notificationData.unique = unique;
    return this;
  }

  /**
   * Whether the notification has a progressbar or not.
   * @param showProgressBar boolean. Default is true
   * @returns this
   */
  showProgressBar(showProgressBar: boolean): this {
    this._notificationData.showProgressBar = showProgressBar;
    return this;
  }

  /**
   * Determine progress bar color.
   * @param progressBarColor string. Default is #000000
   * @returns NotificationDataBuilder
   */
  progressBarColor(progressBarColor: string): this {
    this._notificationData.progressBarColor = progressBarColor;
    return this;
  }

  /**
   * Sets extended timeout (duration notification will stay active after user hovers on it)
   * @param extendedTimeOutInSeconds
   * @returns NotificationDataBuilder
   */
  extendedTimeout(extendedTimeOutInSeconds: number): this {
    this._notificationData.extendedTimeOut = extendedTimeOutInSeconds * 1000;
    return this;
  }

  /**
   * Determines the need to run inside angular's zone then builds the toast
   * @param onActivateTick
   * @returns NotificationDataBuilder
   */
  onActivateTick(onActivateTick: boolean): this {
    this._notificationData.onActivateTick = onActivateTick;
    return this;
  }

  /**
   * Allows action buttons to remain visible when observer mode is active.
   */
  allowActionsForObservers(allowActionsForObservers: boolean = true): this {
    this._notificationData.allowActionsForObservers = allowActionsForObservers;
    return this;
  }
}

export class NotificationDataBuilder extends BaseNotificationDataBuilder<NotificationData> {
  constructor(notificationCode: string) {
    super(notificationCode);
    const defaultTitleElement = new IconMessageToasterElement(undefined, 'Default title');

    // Set additional fields specific to NotificationData
    this._notificationData.topElements = [defaultTitleElement];
    this._notificationData.middleElements = [];
    this._notificationData.bottomElements = [];
    this._notificationData.width = 235;
    this._notificationData.height = 'fit-content';
    this._notificationData.showOnMobile = true;

    this.setVersion(NotificationVersion.V1);
  }

  build() {
    if (this._notificationData.progressBarColor === undefined) {
      this._notificationData.progressBarColor = '#000000';
    }

    if (!this._notificationData.timeout) {
      this._notificationData.showProgressBar = false;
    }

    return this._notificationData;
  }

  /**
   * Sets the top elements array of the notification
   * @param topElements [IconMessageToasterElement]
   * @returns NotificationDataBuilder
   */
  topElements(topElements: [IconMessageToasterElement]): NotificationDataBuilder {
    this._notificationData.topElements = topElements;
    return this;
  }

  /**
   * Sets the top right elements in the dropdown menu of the notification
   * @param topRightOptions Option[]
   * @returns NotificationDataBuilder
   */
  topRightOptions(topRightOptions: TopRightOptions): NotificationDataBuilder {
    this._notificationData.topRightOptions = topRightOptions;
    return this;
  }

  /**
   * Sets the middle elements array of the notification
   * @param middleElements [IconMessageToasterElement]
   * @returns NotificationDataBuilder
   */
  middleElements(middleElements: IconMessageToasterElement[]): NotificationDataBuilder {
    this._notificationData.middleElements = middleElements;
    return this;
  }

  /**
   * Sets the bottom elements (buttons) array of the notification
   * @param bottomElements ButtonToasterElement[]
   * @param bottomElementsStacked boolean (is we want the element to be at the top of each others rather than beside)
   * @returns NotificationDataBuilder
   */
  bottomElements(
    bottomElements: ButtonToasterElement[],
    bottomElementsStacked: boolean = false,
  ): NotificationDataBuilder {
    this._notificationData.bottomElements = bottomElements;
    this._notificationData.bottomElementsStacked = bottomElementsStacked;
    return this;
  }

  /**
   * Sets the button in the bottom elements with a dropdown menu in the notification
   * @param bottomElementOptions BottomElementOptions (string, Option[])
   * @returns NotificationDataBuilder
   */
  bottomElementOptionsButton(bottomElementOptions: BottomElementOptions): NotificationDataBuilder {
    this._notificationData.bottomElementButtonOptions = bottomElementOptions;
    return this;
  }

  /**
   * Sets the width of notification.
   * @param width number. Default is 229
   * @returns NotificationDataBuilder
   */
  width(width: number): NotificationDataBuilder {
    this._notificationData.width = width;
    return this;
  }

  /**
   * Sets the height style of notification might be style values or 'number.px'.
   * @param height string. Default is fit-content
   * @returns NotificationDataBuilder
   */
  height(height: string): NotificationDataBuilder {
    this._notificationData.height = height;
    return this;
  }

  /**
   * Whether to show the notification on mobile or not (Not ready yet)
   * @param showOnMobile boolean. Default is true
   * @returns NotificationDataBuilder
   */
  showOnMobile(showOnMobile: boolean): NotificationDataBuilder {
    this._notificationData.showOnMobile = showOnMobile;
    return this;
  }
}

export class V2NotificationDataBuilder extends BaseNotificationDataBuilder<V2NotificationData> {
  constructor(notificationCode: string) {
    super(notificationCode);
    this.setVersion(NotificationVersion.V2);
  }

  build() {
    // Set default icons for primary and secondary buttons
    this.setDefaultIconForButton(this._notificationData.actionButtonsConfig?.primary);
    this.setDefaultIconForButton(this._notificationData.actionButtonsConfig?.secondary);

    if (this._notificationData.progressBarColor === undefined) {
      this._notificationData.progressBarColor = '#BBD8FF';
    }

    if (!this._notificationData.timeout) {
      this._notificationData.showProgressBar = false;
    }

    return this._notificationData;
  }

  /**
   * title of the notification, it'll de displayed as a bold text on the top
   * @param title
   */
  title(title: string): this {
    this._notificationData.title = title;
    return this;
  }

  /**
   * the icon used for the notification on the top left
   * @param icon
   */
  icon(icon: NotificationIconConfig): this {
    this._notificationData.icon = icon;
    return this;
  }

  /**
   * description of the notification message itself
   * @param description
   */
  description(description: string): this {
    this._notificationData.description = description;
    return this;
  }

  /**
   * primary and secondary buttons config for the notification
   * @param actionButtonsConfig
   */
  actionButtonsConfig(actionButtonsConfig: NotificationActionButtonsConfig): this {
    this._notificationData.actionButtonsConfig = actionButtonsConfig;
    return this;
  }

  /**
   * Observable that emits progress values from 0-100 for the action area progress bar
   * @param actionProgressBarObservable
   */
  actionProgressBarObservable(actionProgressBarObservable: Observable<number>): this {
    this._notificationData.actionProgressBarObservable = actionProgressBarObservable;
    return this;
  }

  /**
   * set the default icon for primary or secondary buttons if it isn't provided
   * @param button
   * @private
   */
  private setDefaultIconForButton(button: NotificationActionButton | undefined) {
    if (button && !button.icon && !button.suppressDefaultActionIcon) {
      button.icon = 'keyboard_arrow_right';
      button.iconConfig = {
        type: IconTypes.matIcon,
        outlined: true,
      };
    }
  }
}

type NotificationDataUnion = NotificationData | V2NotificationData;

export enum NotificationType {
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
  SUCCESS = 'success',
  REQUEST = 'request',
}

export interface NotificationDataElementOption {
  text?: string;
  icon?: {
    icon: string;
    size: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class NotificationToasterService {
  private notificationCategoriesMap: Map<string, Set<number>>;
  private currentNotifications: Map<number, BaseNotificationData>;
  private pendingShowNotificationIds: Map<string, Set<number>>;
  private pendingDismissCodes: Set<string>;
  private sourceURL?: string;
  private destinationURL?: string;
  private routerSubscription: Subscription;
  private showNotificationMutex = new Mutex();
  private loadingNotificationCounter: any = {};

  private readonly LOADING_NOTIFICATION_TIMOUT_IN_SECONDS = 15;

  constructor(
    private readonly toastrService: ToastrService,
    private readonly router: Router,
    private readonly telemetry: TelemetryService,
    private readonly inspectModeRepository: InspectModeRepository,
    private readonly userService: UserService,
  ) {
    this.notificationCategoriesMap = new Map();
    this.currentNotifications = new Map();
    this.pendingShowNotificationIds = new Map<string, Set<number>>();
    this.pendingDismissCodes = new Set<string>();
    this.sourceURL = this.router.url;
    this.destinationURL = this.router.url;
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.sourceURL = this.router.url;
      }

      if (event instanceof NavigationEnd) {
        this.destinationURL = this.router.url;
      }
      if (this.routeChanged()) {
        this.dismissAllNotifications();
      }
    });
  }

  private routeChanged(): boolean {
    // handling the copy/paste url case
    if (this.sourceURL === '/') {
      return false;
    }
    return this.sourceURL?.split('/')[1] != this.destinationURL?.split('/')[1];
  }

  public getActiveNotificationsCount() {
    return this.currentNotifications.size;
  }

  public async showNotification(notificationData: NotificationDataUnion, onHidden?: () => void) {
    if (this.inspectModeRepository.isInspectMode) {
      return;
    }
    notificationData = this.getObserverSafeNotificationData(notificationData);
    const isV2Notification = notificationData.version === NotificationVersion.V2;

    await this.showNotificationMutex.runExclusive(() => {
      this.telemetryLogger(notificationData, isV2Notification);
      let toast: ActiveToast<any>;
      const notificationDataStack: BaseNotificationData[] = [];

      if (this.isDuplicateNotification(notificationData)) {
        return;
      }
      // pop lower priority notifications
      if (this.toastrService.toasts.length > 0) {
        let toastIndex = this.toastrService.toasts.length - 1;
        while (
          toastIndex >= 0 &&
          this.currentNotifications &&
          this.currentNotifications.get(this.toastrService.toasts[toastIndex].toastId) &&
          this.currentNotifications.get(this.toastrService.toasts[toastIndex].toastId)!.priority &&
          notificationData.priority &&
          this.currentNotifications.get(this.toastrService.toasts[toastIndex].toastId)!.priority! >
            notificationData.priority
        ) {
          notificationDataStack.push(
            this.currentNotifications.get(this.toastrService.toasts[toastIndex].toastId)!,
          );
          this.toastrService.remove(this.toastrService.toasts[toastIndex].toastId);
          toastIndex--;
        }
      }

      switch (notificationData.type) {
        case NotificationType.ERROR:
          toast = this.showErrorNotification(notificationData, isV2Notification);
          break;
        case NotificationType.WARNING:
          toast = this.showWarningNotification(notificationData, isV2Notification);
          break;
        case NotificationType.INFO:
          toast = this.showInfoNotification(notificationData, isV2Notification);
          break;
        case NotificationType.SUCCESS:
          toast = this.showSuccessNotification(notificationData, isV2Notification);
          break;
        case NotificationType.REQUEST:
          toast = this.showRequestNotification(notificationData, isV2Notification);
          break;
        default:
          toast = this.showInfoNotification(notificationData, isV2Notification);
      }
      if (!this.pendingShowNotificationIds.has(notificationData.notificationCode)) {
        this.pendingShowNotificationIds.set(notificationData.notificationCode, new Set());
      }
      this.pendingShowNotificationIds.get(notificationData.notificationCode)?.add(toast.toastId);
      toast.onShown.subscribe(() => {
        // handle dimissing notifications that were not shown yet
        this.pendingShowNotificationIds
          .get(notificationData.notificationCode)
          ?.delete(toast.toastId);
        let notificationItems = this.notificationCategoriesMap.get(
          notificationData.notificationCode,
        );
        if (this.pendingDismissCodes.has(notificationData.notificationCode) && notificationItems) {
          this.pendingDismissCodes.delete(notificationData.notificationCode);
          notificationItems.forEach((item) => this.toastrService.remove(item));
          this.notificationCategoriesMap.set(notificationData.notificationCode, new Set<number>());
          notificationItems.clear();
        }
        // manage notifications state
        if (notificationItems) {
          notificationItems.add(toast.toastId);
        } else {
          notificationItems = new Set<number>();
          notificationItems.add(toast.toastId);
        }
        this.notificationCategoriesMap.set(notificationData.notificationCode, notificationItems);
        this.currentNotifications.set(toast.toastId, notificationData);
      });
      const s = toast.onHidden.subscribe(() => {
        this.currentNotifications.delete(toast.toastId);
        const notificationItems = this.notificationCategoriesMap.get(
          notificationData.notificationCode,
        );
        if (notificationItems) {
          notificationItems.delete(toast.toastId);
          if (notificationItems.size == 0) {
            this.notificationCategoriesMap.delete(notificationData.notificationCode);
          }
        }
        if (onHidden) {
          onHidden();
        }
        s.unsubscribe(); // unsubscribe when the toast is gone
      });

      // redisplay the popped notifications
      while (notificationDataStack.length > 0) {
        this.showNotification(notificationDataStack.pop()!);
      }
    });
  }

  private getObserverSafeNotificationData(
    notificationData: NotificationDataUnion,
  ): NotificationDataUnion {
    if (
      notificationData.allowActionsForObservers ||
      !isObserverTabId(this.userService.userUniqueHash)
    ) {
      return notificationData;
    }

    if (notificationData.version === NotificationVersion.V2) {
      const v2NotificationData = notificationData as V2NotificationData;
      delete v2NotificationData.actionButtonsConfig;
      return v2NotificationData;
    }

    const v1NotificationData = notificationData as NotificationData;
    delete v1NotificationData.topRightOptions;
    v1NotificationData.bottomElements = [];
    delete v1NotificationData.bottomElementButtonOptions;
    return v1NotificationData;
  }

  /**
   * get the default title and message used when displayed the toaster with the toast service
   * @param notificationData
   * @param isV2Notification
   * @private
   */
  private getNotificationTitleAndMessage(
    notificationData: NotificationDataUnion,
    isV2Notification = false,
  ) {
    const title = isV2Notification
      ? (notificationData as V2NotificationData).title
      : (notificationData as NotificationData).topElements[0]?.msg;
    const message = isV2Notification
      ? (notificationData as V2NotificationData).description
      : (notificationData as NotificationData).middleElements[0]?.msg;
    return { title, message };
  }

  /**
   * set default icon for the toast notification if icon wasn't provided ( for V1 only )
   * @param notificationData
   * @param icon
   * @param isV2Notification
   * @private
   */
  private setDefaultIcon(
    notificationData: NotificationDataUnion,
    icon: string,
    isV2Notification = false,
  ): void {
    if (isV2Notification) {
      return;
    }

    if (
      !(notificationData as NotificationData).topElements[0].icon &&
      !(notificationData as NotificationData).topElements[0].spinner
    ) {
      (notificationData as NotificationData).topElements[0].icon = { icon, size: 16 };
    }
  }

  /**
   * get the common toast options used for differnt notifcation types
   * @param notificationData
   * @param isV2Notification
   * @private
   */
  private getToastOptions(notificationData: NotificationDataUnion, isV2Notification: boolean): any {
    // Build toast class: base class + dark class (if applicable) + custom classes
    const darkClass = notificationData.style === ToasterPopupStyle.DARK ? ' toast-dark' : '';
    const customClasses = notificationData.toastClass ? ` ${notificationData.toastClass}` : '';
    const toastClass = `${TOAST_BASE_CLASS} ${darkClass} ${customClasses}`;

    return {
      tapToDismiss: false,
      disableTimeOut: !notificationData.timeout,
      timeOut: notificationData.timeout,
      payload: notificationData,
      toastComponent: isV2Notification
        ? CustomNotificationToasterV2Component
        : CustomNotificationToastrComponent,
      progressBar: false,
      progressAnimation: 'increasing',
      toastClass,
      extendedTimeOut: notificationData.extendedTimeOut,
      onActivateTick: notificationData.onActivateTick,
    };
  }

  /**
   * one common method will be used for different notification to display notification
   * used to avoid duplication and repetative code
   * @param notificationData
   * @param isV2Notification
   * @param type
   * @param defaultIcon
   * @private
   */
  private displayNotification(
    notificationData: NotificationDataUnion,
    isV2Notification: boolean,
    type: NotificationType,
    defaultIcon: string,
  ): ActiveToast<any> {
    // Set default icon if not present
    this.setDefaultIcon(notificationData, defaultIcon, isV2Notification);

    const titlePrefix = type.toUpperCase();
    const { title, message } = this.getNotificationTitleAndMessage(
      notificationData,
      isV2Notification,
    );
    const toastTitle = `${titlePrefix}:${title}`;

    const toastOptions = this.getToastOptions(notificationData, isV2Notification);

    switch (notificationData.type) {
      case NotificationType.ERROR:
        return this.toastrService.error(message, toastTitle, toastOptions);
      case NotificationType.WARNING:
        return this.toastrService.warning(message, toastTitle, toastOptions);
      case NotificationType.INFO:
        return this.toastrService.info(message, toastTitle, toastOptions);
      case NotificationType.SUCCESS:
        return this.toastrService.success(message, toastTitle, toastOptions);
      case NotificationType.REQUEST:
        return this.toastrService.info(message, toastTitle, toastOptions);
      default:
        return this.toastrService.info(message, toastTitle, toastOptions);
    }
  }

  private showErrorNotification(
    notificationData: NotificationDataUnion,
    isV2Notification = false,
  ): ActiveToast<any> {
    return this.displayNotification(
      notificationData,
      isV2Notification,
      NotificationType.ERROR,
      'warning',
    );
  }

  private showWarningNotification(
    notificationData: NotificationDataUnion,
    isV2Notification = false,
  ) {
    return this.displayNotification(
      notificationData,
      isV2Notification,
      NotificationType.WARNING,
      'warning',
    );
  }

  private showInfoNotification(notificationData: NotificationDataUnion, isV2Notification = false) {
    return this.displayNotification(
      notificationData,
      isV2Notification,
      NotificationType.INFO,
      'info',
    );
  }

  private showSuccessNotification(
    notificationData: NotificationDataUnion,
    isV2Notification = false,
  ) {
    return this.displayNotification(
      notificationData,
      isV2Notification,
      NotificationType.SUCCESS,
      'check',
    );
  }

  private showRequestNotification(
    notificationData: NotificationDataUnion,
    isV2Notification = false,
  ) {
    return this.displayNotification(
      notificationData,
      isV2Notification,
      NotificationType.REQUEST,
      'info',
    );
  }

  public showLoadingNotification(
    text: string,
    notificationCode: string,
    autoDismiss = false,
    isError = false,
    onTimedOut?: () => void,
    onActivateTick?: boolean,
  ): void {
    const notificationAlreadyShowing = this.checkIfNotificationActiveByCode(notificationCode);

    if (!notificationAlreadyShowing) {
      this.loadingNotificationCounter[notificationCode] = 0;
    }

    const numberOfShownNotifications = this.loadingNotificationCounter[notificationCode] ?? 0;

    this.loadingNotificationCounter[notificationCode] = numberOfShownNotifications + 1;

    if (!notificationAlreadyShowing) {
      // show notification on the first time only

      // Configure spinner icon for V2 notification
      const notificationIcon: NotificationIconConfig = {
        type: NotificationIconTypes.SPINNER,
        size: 18,
        background: isError ? IconBackground.ERROR : IconBackground.INFO,
      };

      // Build V2 notification with spinner - type is determined by isError flag
      const notificationDataBuilder = new V2NotificationDataBuilder(notificationCode)
        .style(isError ? ToasterPopupStyle.ERROR : ToasterPopupStyle.INFO)
        .type(isError ? NotificationType.ERROR : NotificationType.INFO)
        .title(text)
        .icon(notificationIcon)
        .dismissable(false);

      if (onActivateTick) {
        // Loading toasts are shown right before async work; without a forced tick
        // ngx-toastr can wait until that work finishes before painting.
        notificationDataBuilder.onActivateTick(true);
      }

      if (autoDismiss) {
        notificationDataBuilder.timeOut(this.LOADING_NOTIFICATION_TIMOUT_IN_SECONDS);
      }

      this.showNotification(notificationDataBuilder.build(), () => {
        if (this.loadingNotificationCounter[notificationCode] && onTimedOut) {
          onTimedOut();
        }
      });
    }
  }

  public dismissLoadingNotification(notificationCode: string): void {
    if (!this.checkIfNotificationActiveByCode(notificationCode)) {
      this.loadingNotificationCounter[notificationCode] = 0;
      return;
    }

    if (this.loadingNotificationCounter[notificationCode]) {
      this.loadingNotificationCounter[notificationCode] -= 1;
    }
    if (this.loadingNotificationCounter[notificationCode] === 0) {
      this.dismissNotificationsByCode([notificationCode]);
    }
  }

  /**
   * Shows a progress bar notification that updates based on an observable
   * @param title - The notification title
   * @param notificationCode - Unique notification code
   * @param progressObservable$ - Observable that emits progress values (0-100)
   * @param onCancel - Optional cancel callback (triggered when notification is dismissed)
   */
  public showProgressBarNotification(
    title: string,
    notificationCode: string,
    progressObservable$: Observable<number>,
    onCancel?: () => void,
  ): void {
    // Don't show duplicate notifications
    if (this.checkIfNotificationActiveByCode(notificationCode)) {
      return;
    }

    const notificationIcon: NotificationIconConfig = {
      assetName: 'folder_open',
      type: NotificationIconTypes.MAT_ICON,
      size: 18,
      background: IconBackground.INFO,
    };

    const notificationData = new V2NotificationDataBuilder(notificationCode)
      .type(NotificationType.INFO)
      .style(ToasterPopupStyle.INFO)
      .title(title)
      .icon(notificationIcon)
      .actionProgressBarObservable(progressObservable$)
      .dismissable(!!onCancel)
      .priority(500)
      .build();

    // Show notification with onHidden callback for cancellation
    this.showNotification(notificationData, onCancel);
  }

  public dismissNotificationsByCode(notificationCodes: string[]) {
    // delay the action till the notifications are rendered
    notificationCodes.forEach((code) => {
      const notificationItems = this.notificationCategoriesMap.get(code);
      if (notificationItems) {
        notificationItems.forEach((item) => {
          this.toastrService.remove(item);
        });
      } else if (this.pendingShowNotificationIds.has(code)) {
        this.pendingDismissCodes.add(code);
      }
    });
  }

  public checkIfNotificationActiveByCode(notificationCode: string): boolean {
    const notificationItems = this.notificationCategoriesMap.get(notificationCode);
    if (notificationItems && notificationItems.size > 0) {
      return true;
    } else {
      return false;
    }
  }

  public dismissAllNotifications() {
    this.toastrService.clear();
  }

  ngOnDestroy() {
    this.routerSubscription.unsubscribe();
  }

  private telemetryLogger(notificationData: NotificationDataUnion, isV2Notification = false) {
    const { type, notificationCode } = notificationData;
    const { title, message } = this.getNotificationTitleAndMessage(
      notificationData,
      isV2Notification,
    );
    this.telemetry.event('Notification Event', {
      type,
      title,
      message,
      code: notificationCode,
    });
  }

  private isDuplicateNotification(notificationData: NotificationDataUnion) {
    return this.notificationCategoriesMap.has(notificationData.notificationCode);
  }

  showDefaultSuccessNotification(
    messageContent: string,
    notificationCode: string,
    activateTick: boolean = false,
    toastClass: string = '',
    customizeBuilder?: (builder: V2NotificationDataBuilder) => V2NotificationDataBuilder,
  ) {
    const notificationIcon: NotificationIconConfig = {
      assetName: 'check',
      size: 18,
      type: NotificationIconTypes.MAT_ICON,
      outlined: true,
      background: IconBackground.SUCCESS,
    };

    // Start building with defaults
    let successNotificationDataBuilder = new V2NotificationDataBuilder(notificationCode)
      .style(ToasterPopupStyle.SUCCESS)
      .type(NotificationType.SUCCESS)
      .title(messageContent)
      .icon(notificationIcon)
      .priority(380)
      .timeOut(5) // Default timeout in seconds for the builder
      .onActivateTick(activateTick)
      .toastClass(toastClass);

    // Apply customization *using the builder*, if provided
    if (customizeBuilder) {
      successNotificationDataBuilder = customizeBuilder(successNotificationDataBuilder);
    }

    // Build the final data from the (potentially customized) builder
    const notificationData = successNotificationDataBuilder.build();

    // Show the notification
    this.showNotification(notificationData);
  }

  showDefaultErrorNotificationV2WithBuilder(
    titleMsg: string,
    notificationDescription: string,
    notificationCode: string,
    customizeBuilder?: (builder: V2NotificationDataBuilder) => V2NotificationDataBuilder,
    onTimedOut: () => void = () => {},
  ): void {
    const notificationIcon: NotificationIconConfig = {
      assetName: 'warning_amber',
      size: 18,
      type: NotificationIconTypes.MAT_ICON,
      outlined: true,
      background: IconBackground.ERROR,
    };

    let errorNotificationDataBuilder = new V2NotificationDataBuilder(notificationCode)
      .style(ToasterPopupStyle.ERROR)
      .type(NotificationType.ERROR)
      .timeOut(10)
      .title(titleMsg)
      .description(notificationDescription)
      .icon(notificationIcon)
      .onActivateTick(false)
      .toastClass('');

    if (customizeBuilder) {
      errorNotificationDataBuilder = customizeBuilder(errorNotificationDataBuilder);
    }

    const notificationData = errorNotificationDataBuilder.build();
    this.showNotification(notificationData, onTimedOut);
  }

  /**
   * This is the most comprehensive function for Notification V2, containing the most used values.
   */
  showDefaultErrorNotificationV2(
    titleMsg: string,
    notificationDescription: string,
    notificationCode: string,
    notificationIconConfig: NotificationIconConfig = {
      assetName: 'warning_amber',
      size: 18,
      type: NotificationIconTypes.MAT_ICON,
      outlined: true,
    },
    buttons?: NotificationActionButtonsConfig,
    timeOut: number = 10,
    onActivateTick: boolean = false, // For more info, please refer to https://pencilapp.slack.com/archives/G01KJ1DC249/p1725300624486079
    onTimedOut: () => void = () => {},
    toastClass: string = '',
  ): void {
    const notificationDataBuilder = new V2NotificationDataBuilder(notificationCode)
      .style(ToasterPopupStyle.ERROR)
      .type(NotificationType.ERROR)
      .timeOut(timeOut)
      .title(titleMsg)
      .description(notificationDescription)
      .onActivateTick(onActivateTick)
      .toastClass(toastClass);

    if (notificationIconConfig) {
      notificationIconConfig.background = IconBackground.ERROR;
      notificationDataBuilder.icon(notificationIconConfig);
    }

    if (buttons) {
      notificationDataBuilder.actionButtonsConfig(buttons);
    }

    const notificationData = notificationDataBuilder.build();
    this.showNotification(notificationData, onTimedOut);
  }

  showDefaultWarningNotificationV2(
    titleMsg: string,
    contentMsg: string,
    notificationCode: string,
    notificationIconConfig: NotificationIconConfig = {
      assetName: 'warning_amber',
      size: 18,
      type: NotificationIconTypes.MAT_ICON,
      background: IconBackground.WARNING,
      outlined: true,
    },
    buttons?: NotificationActionButtonsConfig,
    timeOut?: number,
    onActivateTick: boolean = false, // For more info, please refer to https://pencilapp.slack.com/archives/G01KJ1DC249/p1725300624486079
  ): void {
    const notificationDataBuilder = new V2NotificationDataBuilder(notificationCode)
      .style(ToasterPopupStyle.WARN)
      .type(NotificationType.WARNING)
      .toastClass('toast-v2-warning')
      .title(titleMsg)
      .description(contentMsg)
      .onActivateTick(onActivateTick);

    if (timeOut) {
      notificationDataBuilder.timeOut(timeOut);
    }

    if (notificationIconConfig) {
      notificationDataBuilder.icon(notificationIconConfig);
    }

    if (buttons) {
      notificationDataBuilder.actionButtonsConfig(buttons);
    }

    const notificationData = notificationDataBuilder.build();
    this.showNotification(notificationData);
  }

  /**
   * Shows a standardized info notification with V2 styling
   * This follows the same API format as showDefaultErrorNotificationV2
   */
  showDefaultInfoNotificationV2(
    titleMsg: string,
    notificationDescription: string,
    notificationCode: string,
    notificationIconConfig: NotificationIconConfig = {
      assetName: 'info',
      size: 18,
      type: NotificationIconTypes.MAT_ICON,
      outlined: true,
      background: IconBackground.INFO,
    },
    buttons?: NotificationActionButtonsConfig,
    timeOut: number = 5,
    allowActionsForObservers: boolean = false,
    onActivateTick: boolean = false, // For more info, please refer to https://pencilapp.slack.com/archives/G01KJ1DC249/p1725300624486079
    onTimedOut: () => void = () => {},
    toastClass: string = '',
  ): void {
    const notificationDataBuilder = new V2NotificationDataBuilder(notificationCode)
      .style(ToasterPopupStyle.INFO)
      .type(NotificationType.INFO)
      .timeOut(timeOut)
      .title(titleMsg)
      .description(notificationDescription)
      .onActivateTick(onActivateTick)
      .toastClass(toastClass);

    if (notificationIconConfig) {
      notificationIconConfig.background = IconBackground.INFO;
      notificationDataBuilder.icon(notificationIconConfig);
    }

    if (buttons) {
      notificationDataBuilder.actionButtonsConfig(buttons);
    }

    if (allowActionsForObservers) {
      notificationDataBuilder.allowActionsForObservers();
    }

    const notificationData = notificationDataBuilder.build();
    this.showNotification(notificationData, onTimedOut);
  }

  /**
   * Generates a unique notification code in the format "Notification-XXXXX" where X is a random digit
   * @returns A string containing "Notification-" prefix followed by a 5-digit random number padded with leading zeros
   * @example "Notification-00123", "Notification-45678"
   */
  generateNotificationCode(): string {
    const randomDigits = Math.floor(Math.random() * 100000)
      .toString()
      .padStart(5, '0');
    return `Notification-${randomDigits}`;
  }
}
